// src/lib/calendarRepository.ts
import { turso } from '@/database/client';

export interface CalendarEvent {
  id: string;
  user_id: string;
  title: string;
  description?: string;
  due_date: string;
  course?: string;
  status?: string;
  category?: string;
  notes?: string;
  completed?: boolean;
  completed_at?: string;
  created_at?: string;
  updated_at?: string;
}

/**
 * Create a new calendar event
 */
export async function createCalendarEvent(
  event: {
    user_id: string;
    title: string;
    date: string;
    startTime?: string;
    endTime?: string;
    location?: string;
    description?: string;
    category?: string;
    course?: string | null;
    status?: string;
    notes?: string;
    metadata?: any;
  }
): Promise<CalendarEvent> {
  const id = `event_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  
  await turso.execute({
    sql: `INSERT INTO calendar_events (id, user_id, title, description, due_date, course, status, category, notes, created_at, updated_at)
          VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, datetime('now'), datetime('now'))`,
    args: [
      id,
      event.user_id,
      event.title,
      event.description || null,
      event.date,
      event.course || null, // Explicitly allow null course
      event.status || 'pending',
      event.category || null,
      event.notes || event.description || null
    ]
  });

  return {
    id,
    user_id: event.user_id,
    title: event.title,
    description: event.description,
    due_date: event.date,
    course: event.course || undefined,
    status: event.status || 'pending',
    category: event.category,
    notes: event.notes || event.description || undefined
  };
}

/**
 * Get all calendar events for a user (excludes completed by default)
 */
export async function getCalendarEvents(userId: string, includeCompleted = false): Promise<CalendarEvent[]> {
  // Simplified query - just get all events for now (completed column may not exist yet)
  const sql = 'SELECT * FROM calendar_events WHERE user_id = ? ORDER BY due_date ASC';
  
  const result = await turso.execute({
    sql,
    args: [userId]
  });

  return result.rows.map(row => ({
    id: row.id as string,
    user_id: row.user_id as string,
    title: row.title as string,
    description: row.description as string | undefined,
    due_date: row.due_date as string,
    course: row.course as string | undefined,
    status: row.status as string | undefined,
    category: row.category as string | undefined,
    notes: row.notes as string | undefined,
    created_at: row.created_at as string | undefined,
    updated_at: row.updated_at as string | undefined
  }));
}

/**
 * Update a calendar event
 */
export async function updateCalendarEvent(
  id: string,
  updates: Partial<Omit<CalendarEvent, 'id' | 'user_id' | 'created_at'>>
): Promise<void> {
  const fields: string[] = [];
  const values: any[] = [];

  if (updates.title !== undefined) {
    fields.push('title = ?');
    values.push(updates.title);
  }
  if (updates.description !== undefined) {
    fields.push('description = ?');
    values.push(updates.description);
  }
  if (updates.due_date !== undefined) {
    fields.push('due_date = ?');
    values.push(updates.due_date);
  }
  if (updates.course !== undefined) {
    fields.push('course = ?');
    values.push(updates.course);
  }
  if (updates.status !== undefined) {
    fields.push('status = ?');
    values.push(updates.status);
  }
  if (updates.category !== undefined) {
    fields.push('category = ?');
    values.push(updates.category);
  }
  if (updates.notes !== undefined) {
    fields.push('notes = ?');
    values.push(updates.notes);
  }

  if (fields.length === 0) return;

  fields.push("updated_at = datetime('now')");
  values.push(id);

  await turso.execute({
    sql: `UPDATE calendar_events SET ${fields.join(', ')} WHERE id = ?`,
    args: values
  });
}

/**
 * Mark a calendar event as completed (soft delete)
 */
export async function completeCalendarEvent(id: string): Promise<void> {
  await turso.execute({
    sql: "UPDATE calendar_events SET completed = 1, completed_at = datetime('now'), updated_at = datetime('now') WHERE id = ?",
    args: [id]
  });
}

/**
 * Permanently delete a calendar event
 */
export async function deleteCalendarEvent(id: string): Promise<void> {
  await turso.execute({
    sql: 'DELETE FROM calendar_events WHERE id = ?',
    args: [id]
  });
}

/**
 * Get completed calendar events for review
 */
export async function getCompletedCalendarEvents(userId: string): Promise<CalendarEvent[]> {
  const result = await turso.execute({
    sql: 'SELECT * FROM calendar_events WHERE user_id = ? AND completed = 1 ORDER BY completed_at DESC',
    args: [userId]
  });

  return result.rows.map(row => ({
    id: row.id as string,
    user_id: row.user_id as string,
    title: row.title as string,
    description: row.description as string | undefined,
    due_date: row.due_date as string,
    course: row.course as string | undefined,
    status: row.status as string | undefined,
    category: row.category as string | undefined,
    notes: row.notes as string | undefined,
    completed: Boolean(row.completed),
    completed_at: row.completed_at as string | undefined,
    created_at: row.created_at as string | undefined,
    updated_at: row.updated_at as string | undefined
  }));
}

/**
 * Get calendar events for a specific date range
 */
export async function getCalendarEventsByDateRange(
  userId: string,
  startDate: string,
  endDate: string
): Promise<CalendarEvent[]> {
  const result = await turso.execute({
    sql: 'SELECT * FROM calendar_events WHERE user_id = ? AND due_date >= ? AND due_date <= ? ORDER BY due_date ASC',
    args: [userId, startDate, endDate]
  });

  return result.rows.map(row => ({
    id: row.id as string,
    user_id: row.user_id as string,
    title: row.title as string,
    description: row.description as string | undefined,
    due_date: row.due_date as string,
    course: row.course as string | undefined,
    status: row.status as string | undefined,
    category: row.category as string | undefined,
    notes: row.notes as string | undefined,
    created_at: row.created_at as string | undefined,
    updated_at: row.updated_at as string | undefined
  }));
}

/**
 * Migrate localStorage calendar events to database
 */
export async function migrateLocalStorageCalendar(userId: string): Promise<number> {
  try {
    // Get existing events from localStorage
    const localData = localStorage.getItem('whipt_class_notebooks');
    if (!localData) return 0;

    const notebooks = JSON.parse(localData);
    let migratedCount = 0;

    // Extract all assignments from notebooks
    for (const notebook of notebooks) {
      for (const category of notebook.categories || []) {
        for (const item of category.items || []) {
          if (item.dueDate) {
            try {
              await createCalendarEvent({
                user_id: userId,
                title: item.title || 'Untitled',
                description: item.notes || '',
                due_date: item.dueDate,
                course: notebook.course || notebook.title,
                status: item.status || 'pending',
                category: category.name,
                notes: item.notes
              });
              migratedCount++;
            } catch (err) {
              console.error('Failed to migrate event:', item.title, err);
            }
          }
        }
      }
    }

    console.log(`✅ Migrated ${migratedCount} calendar events to database`);
    return migratedCount;
  } catch (error) {
    console.error('Failed to migrate calendar events:', error);
    return 0;
  }
}
